import { useState, type FormEvent } from 'react';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/dahcek3p14a1h3mr6ijg';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const topicOptions = ['提供案件线索', '求助咨询', '内容纠错', '合作与转载', '其他'];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    setFormError('');
    setSuccessMsg('');

    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setSuccessMsg('感谢你的反馈，我们已收到。');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string') payload.append(key, value);
    });

    setStatus('submitting');
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const code = parsed?.code;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        setSuccessMsg(parsed?.meta?.message || '提交成功，我们会尽快查看你提供的信息。');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功，请稍后重试。');
    }
  };

  return (
    <form
      id="tip-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-xl border border-background-200 bg-background-50 p-5 md:p-8"
    >
      <h3 className="font-heading text-lg font-bold text-foreground-950">提供线索 / 联系我们</h3>
      <p className="mt-2 text-xs leading-relaxed text-foreground-600">
        如果你掌握可核实的公开信息，或对站内内容有更正建议，欢迎通过下面的表单告诉我们。带 * 的为必填项。
      </p>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <label htmlFor="tip-name" className="text-xs font-medium text-foreground-700">
            你的称呼 <span className="text-primary-600">*</span>
          </label>
          <input
            id="tip-name"
            name="name"
            type="text"
            required
            placeholder="如何称呼你"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div className="flex flex-col gap-2">
          <label htmlFor="tip-email" className="text-xs font-medium text-foreground-700">
            联系邮箱 <span className="text-primary-600">*</span>
          </label>
          <input
            id="tip-email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
        <div className="flex flex-col gap-2 sm:col-span-2">
          <label htmlFor="tip-topic" className="text-xs font-medium text-foreground-700">
            事项类型 <span className="text-primary-600">*</span>
          </label>
          <select
            id="tip-topic"
            name="topic"
            required
            defaultValue=""
            className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400"
          >
            <option value="" disabled>
              请选择事项类型
            </option>
            {topicOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-2 sm:col-span-2">
          <label htmlFor="tip-detail" className="text-xs font-medium text-foreground-700">
            详细说明 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="tip-detail"
            name="detail"
            required
            maxLength={500}
            rows={5}
            placeholder="请尽量说明时间、地点与信息来源，便于我们核实（不超过 500 字）"
            className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
          />
        </div>
      </div>

      <input
        className="form-utility-note"
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="flex items-start gap-2 text-[11px] leading-relaxed text-foreground-500">
          <i className="ri-lock-line mt-0.5"></i>
          我们仅将信息用于内容核实，不会对外披露你的个人联系方式。
        </p>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              提交中
            </>
          ) : (
            <>
              <i className="ri-send-plane-line"></i>
              提交信息
            </>
          )}
        </button>
      </div>

      {status === 'success' && successMsg && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-xs leading-relaxed text-accent-900">
          <i className="ri-checkbox-circle-line mt-0.5 text-base"></i>
          {successMsg}
        </p>
      )}
      {status === 'error' && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-xs leading-relaxed text-primary-900">
          <i className="ri-error-warning-line mt-0.5 text-base"></i>
          {formError}
        </p>
      )}
    </form>
  );
}